<script setup>
// §8.4 DurationInput — enforces the firm's billing increment, shows actual +
// rounded (Mobile spec §6.3). Numerals are tabular (§5). Rounding is disclosed,
// never silent — an approver and a timekeeper must see both.
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Number, default: 0 }, // minutes, actual
  increment: { type: Number, default: 6 }, // firm billing increment, minutes (6 = 1/10h)
})
const emit = defineEmits(['update:modelValue'])

const raw = ref(props.modelValue)
watch(() => props.modelValue, (v) => (raw.value = v))

const rounded = computed(() => Math.round(raw.value / props.increment) * props.increment)
const fmt = (m) => {
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${h}h ${String(min).padStart(2, '0')}m`
}
const changed = computed(() => rounded.value !== raw.value)

function onInput(e) {
  const [h = 0, m = 0] = e.target.value.split(':').map((n) => parseInt(n, 10) || 0)
  raw.value = h * 60 + m
  emit('update:modelValue', rounded.value)
}
const hhmm = computed(() => {
  const h = Math.floor(raw.value / 60)
  const m = raw.value % 60
  return `${h}:${String(m).padStart(2, '0')}`
})
</script>

<template>
  <div class="l-duration">
    <div class="field">
      <input class="num" type="text" inputmode="numeric" :value="hhmm" @change="onInput" aria-label="Duration, hours and minutes" />
      <span class="unit">h:m</span>
    </div>
    <div class="disclose">
      <span class="actual num">Actual {{ fmt(raw) }}</span>
      <span class="dot" aria-hidden="true">·</span>
      <span class="billed num" :class="{ up: changed }">
        Billed {{ fmt(rounded) }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.l-duration {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.field {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  width: fit-content;
}
input {
  width: 84px;
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: var(--text-base);
  padding: 7px 10px;
  border: 1px solid var(--line);
  border-radius: var(--r);
  background: var(--surface);
  color: var(--text);
}
input:focus-visible {
  border-color: var(--interactive);
}
.unit {
  font-size: var(--text-chip);
  color: var(--text-3);
}
.disclose {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--text-chip);
  color: var(--text-3);
}
.billed {
  font-weight: 600;
  color: var(--text-2);
}
.billed.up {
  color: var(--accent-text);
}
.dot {
  color: var(--decorative);
}
</style>
